import React, { useState } from "react";
import { getImageUrl } from "@/utils/imageUrl";
import { useSelector, useDispatch } from "react-redux";
import { motion } from "framer-motion";
import { Building2, Mail, Phone, MapPin, FileText, CheckCircle, Edit2 } from "lucide-react";
import Badge from "@/components/common/Badge";
import Button from "@/components/common/Button";
import TextInput from "@/components/common/TextInput";
import Modal from "@/components/common/Modal";
import { updateOrganizerEmail } from "@/store/slices/organizerSlice";
import { toast } from "react-toastify";

const SettingsProfile = () => {
  const dispatch = useDispatch();
  const { organizer, isLoading } = useSelector((state) => state.organizer);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [emailForm, setEmailForm] = useState({
    new_email: "",
    current_password: ""
  });

  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    if (!emailForm.new_email || !emailForm.current_password) {
      toast.warn("Please fill all fields");
      return;
    }

    try {
      await dispatch(updateOrganizerEmail(emailForm)).unwrap();
      toast.success("Email updated successfully");
      setIsEditingEmail(false);
      setEmailForm({ new_email: "", current_password: "" });
    } catch (error) {
      toast.error(error.message || "Failed to update email");
    }
  };

  const getRegistrationTypeLabel = (type) => {
    const types = {
      ngo_trust: "NGO / Trust",
      society: "Society",
      company: "Company",
      unregistered: "Unregistered",
    };
    return types[type] || type;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 max-w-2xl"
    >
      <div className="flex items-center gap-4 mb-8">
        <div className="w-16 h-16 rounded-2xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center overflow-hidden">
             {organizer?.logo_url ? (
               <img 
                 src={getImageUrl(organizer.logo_url)} 
                 alt={organizer.organization_name} 
                 className="w-full h-full object-cover" 
               />
             ) : (
               <Building2 className="w-8 h-8 text-amber-600 dark:text-amber-400" />
             )}
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">
            {organizer?.organization_name || "Organization"}
          </h2>
          <div className="flex items-center gap-2 mt-1">
            <Badge tone="organiser" size="sm">
              Organizer
            </Badge>

          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-100 dark:border-gray-700/50">
          <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Email</p>
            <div className="flex items-center gap-3">
              <p className="font-medium text-gray-800 dark:text-gray-100">
                {organizer?.email}
              </p>
              <button
                onClick={() => setIsEditingEmail(true)}
                className="p-1 text-gray-400 hover:text-amber-600 dark:hover:text-amber-400 transition-colors"
                title="Change Email"
              >
                <Edit2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-100 dark:border-gray-700/50">
          <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Phone</p>
            <p className="font-medium text-gray-800 dark:text-gray-100">
              {organizer?.contact_phone || "Not provided"}
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-100 dark:border-gray-700/50">
          <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">City</p>
            <p className="font-medium text-gray-800 dark:text-gray-100">
              {organizer?.city || "Not provided"}
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-100 dark:border-gray-700/50">
          <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Registration Type
            </p>
            <p className="font-medium text-gray-800 dark:text-gray-100">
              {getRegistrationTypeLabel(organizer?.registration_type)}
            </p>
          </div>
        </div>

        {organizer?.registration_number && (
          <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-100 dark:border-gray-700/50">
            <CheckCircle className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Registration Number
              </p>
              <p className="font-medium text-gray-800 dark:text-gray-100">
                {organizer.registration_number}
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl text-sm text-blue-700 dark:text-blue-300">
        To update other details, please contact koset support to prevent unauthorized changes.
      </div>

      {/* Email Update Modal */}
      <Modal
        open={isEditingEmail}
        onClose={() => setIsEditingEmail(false)}
        title="Update Email Address"
        showCloseButton={false}
      >
        <form onSubmit={handleUpdateEmail} className="space-y-4 mt-2">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Please enter your new email address and confirm with your current password.
          </p>
          
          <TextInput
            label="New Email Address"
            type="email"
            placeholder="Enter new email"
            value={emailForm.new_email}
            onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })}
            required
          />
          
          <TextInput
            label="Current Password"
            type="password"
            placeholder="Confirm with password"
            value={emailForm.current_password}
            onChange={(e) => setEmailForm({ ...emailForm, current_password: e.target.value })}
            required
          />

          <div className="flex justify-end gap-3 mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsEditingEmail(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isLoading}
              disabled={isLoading}
            >
              Update Email
            </Button>
          </div>
        </form>
      </Modal>
    </motion.div>
  );
};

export default SettingsProfile;
